import type { AnularOrdenRetiroInput, ExtenderOrdenRetiroInput, OrdenRetiroCreadaDTO, OrdenRetiroDTO, OrdenRetiroInput, RetiroOtraSucursalDTO, RetiroOtraSucursalInput, RetiroOtraSucursalOpcionesDTO, CorreoAutomaticoDTO } from '@mg/shared';
import type { AjusteDTO, AjusteInput, ConteoDTO, DejarPendienteInput, DespieceInput, DespieceVidrioAprobarInput, DespieceVidrioDTO, DespieceVidrioInput, EntregaDTO, PiezaSalidaInput, ProductoDetalleInput, EntregaInput, MovimientoDTO, PendienteEntregaDTO, PiezaDTO, ReingresoDTO, StockListaDTO, ApartadoDTO, VistaLibrosAjusteDTO, VistaLibrosAjusteInput } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post, put } from '@/lib/api.ts';
import { conClave, useEnvioUnico } from '@/lib/idempotencia.ts';

interface Pagina<T> {
  datos: T[];
  total: number;
  pagina: number;
  limite: number;
}
export interface EntregaResumen {
  id: string;
  numero_formateado: string;
  documento_numero: string;
  documento_tipo: string;
  cliente_razon_social: string;
  almacen_nombre: string;
  usuario_nombre: string;
  entregado_en: string;
  lineas: number;
  /** Retiro en otra sucursal (Fase 4): «Venta de …» en la sucursal que despachó, «Retirada en …» en la de venta. */
  retiro_otra_sucursal?: string | null;
}
export interface AjusteResumen {
  id: string;
  numero_formateado: string;
  tipo: string;
  motivo: string;
  estado: string;
  almacen_nombre: string;
  creado_por_nombre: string;
  creado_en: string;
  lineas: number;
  /** null para quien no tiene el permiso de costos. */
  valor_total: string | null;
}
export interface ConteoResumen {
  id: string;
  numero_formateado: string;
  almacen_nombre: string;
  estado: string;
  creado_por_nombre: string;
  creado_en: string;
  confirmado_en: string | null;
  ajuste_numero: string | null;
  lineas: number;
}

const q = (o: Record<string, string | number | boolean | undefined>) => o;

export const useStock = (f: { q?: string | undefined; almacen_id?: string | undefined; sucursal?: string | undefined; solo_con_stock?: boolean | undefined; solo_apartado?: boolean | undefined; pagina?: number; limite?: number }) =>
  useQuery({ queryKey: ['inventario', 'stock', f], queryFn: () => get<StockListaDTO>('/inventario/stock', q({ ...f, solo_con_stock: f.solo_con_stock ? 'true' : undefined, solo_apartado: f.solo_apartado ? 'true' : undefined })), placeholderData: (p) => p });
/** A quién está apartado un producto en un almacén (pedido del 2026-11-01). */
export const useApartado = (fila: { producto_id: string; almacen_id: string } | null) =>
  useQuery({ queryKey: ['inventario', 'apartado', fila], queryFn: () => get<ApartadoDTO>('/inventario/stock/apartado', { producto_id: fila!.producto_id, almacen_id: fila!.almacen_id }), enabled: !!fila });
/**
 * Fila del kardex: `etiqueta` es el nombre a la vista del tipo (distingue la recepción, el faltante que vuelve y la
 * cancelación de un traslado) y `valor_bs` el valor en Bs de una compra, solo para quien ve costos (re-auditoría
 * 2026-10-18, ronda 2, hallazgos 3-H12 y 4-H23). Es el `MovimientoKardexDTO` de la API.
 */
export type MovimientoKardex = MovimientoDTO & { etiqueta?: string; valor_bs?: string | null };
export const useKardex = (productoId: string | null, almacenId?: string) => useQuery({ queryKey: ['inventario', 'kardex', productoId, almacenId], queryFn: () => get<MovimientoKardex[]>(`/inventario/kardex/${productoId}`, q({ almacen_id: almacenId })), enabled: !!productoId });
type FiltrosEntregas = { q?: string | undefined; desde?: string | undefined; hasta?: string | undefined; pagina?: number; limite?: number };
/**
 * Genera (una vez por documento) los avisos de material sin retirar pasado el plazo en días hábiles: a la tienda y a la
 * dueña; desde la Fase 1 de retiros también el correo al cliente (5 días hábiles) y el aviso a la dueña (10).
 */
export const generarAlertasRetiro = () => post<{ generadas: number; avisos_duena?: number; correos?: number }>('/inventario/pendientes/alertas');
export const usePendientes = (f: FiltrosEntregas) => useQuery({ queryKey: ['inventario', 'pendientes', f], queryFn: () => get<Pagina<PendienteEntregaDTO>>('/inventario/pendientes', q(f)), placeholderData: (p) => p });
export const useEntregas = (f: FiltrosEntregas) => useQuery({ queryKey: ['inventario', 'entregas', f], queryFn: () => get<Pagina<EntregaResumen>>('/inventario/entregas', q(f)), placeholderData: (p) => p });
export const useEntrega = (id: string | null) => useQuery({ queryKey: ['inventario', 'entrega', id], queryFn: () => get<EntregaDTO>(`/inventario/entregas/${id}`), enabled: !!id });
export const useAjustes = (f: { estado?: string | undefined; tipo?: string | undefined; pagina?: number; limite?: number }) => useQuery({ queryKey: ['inventario', 'ajustes', f], queryFn: () => get<Pagina<AjusteResumen>>('/inventario/ajustes', q(f)), placeholderData: (p) => p });
export const useAjuste = (id: string | null) => useQuery({ queryKey: ['inventario', 'ajuste', id], queryFn: () => get<AjusteDTO>(`/inventario/ajustes/${id}`), enabled: !!id });
/**
 * Vista previa de los libros de inventario de un ajuste (pedido del 2026-10-30): por guardar (`datos`) o pendiente de
 * aprobar (`id`). Lleva costos: solo se pide con el permiso de costos (`activo`).
 */
export const useVistaLibrosAjuste = (origen: { datos: VistaLibrosAjusteInput } | { id: string } | null, activo: boolean) =>
  useQuery({
    queryKey: ['inventario', 'ajustes', 'vista-libros', origen],
    queryFn: () => (origen && 'id' in origen ? get<VistaLibrosAjusteDTO>(`/inventario/ajustes/${origen.id}/vista-libros`) : post<VistaLibrosAjusteDTO>('/inventario/ajustes/vista-libros', origen!.datos)),
    enabled: !!origen && activo,
    placeholderData: (p) => p,
    retry: false,
  });
export const useConteos = () => useQuery({ queryKey: ['inventario', 'conteos'], queryFn: () => get<Pagina<ConteoResumen>>('/inventario/conteos', q({ limite: 50 })) });
export const useConteo = (id: string | null) => useQuery({ queryKey: ['inventario', 'conteo', id], queryFn: () => get<ConteoDTO>(`/inventario/conteos/${id}`), enabled: !!id });
export const registrarImpresionEntrega = (id: string) => post<{ impresiones: number; copia: boolean }>(`/inventario/entregas/${id}/impresion`);
/** Constancia de pendiente por retirar («Dejar pendiente», Fase 1 de retiros). */
export const useReingreso = (id: string | null) => useQuery({ queryKey: ['inventario', 'reingreso', id], queryFn: () => get<ReingresoDTO>(`/inventario/reingresos/${id}`), enabled: !!id });
export const registrarImpresionReingreso = (id: string) => post<{ impresiones: number; copia: boolean }>(`/inventario/reingresos/${id}/impresion`);

function useInvalidarInventario() {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['inventario'] }), qc.invalidateQueries({ queryKey: ['ventas'] }), qc.invalidateQueries({ queryKey: ['productos'] })]);
}
export function useRegistrarEntrega() {
  const inv = useInvalidarInventario();
  // Reintentar tras un corte de red no registra otra entrega: va la misma clave y el servidor devuelve la que ya hizo
  const enviar = useEnvioUnico(({ documentoId, datos }: { documentoId: string; datos: EntregaInput }, clave: string) => post<EntregaDTO>(`/inventario/documentos/${documentoId}/entregas`, datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
export function useCrearAjuste() {
  const inv = useInvalidarInventario();
  const enviar = useEnvioUnico((d: AjusteInput, clave: string) => post<AjusteDTO>('/inventario/ajustes', d, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
/** «Dejar pendiente» sobre una entrega del mismo día; con la clave de operación (el reintento no lo repite). */
export function useDejarPendiente() {
  const inv = useInvalidarInventario();
  const enviar = useEnvioUnico(({ entregaId, datos }: { entregaId: string; datos: DejarPendienteInput }, clave: string) => post<ReingresoDTO>(`/inventario/entregas/${entregaId}/dejar-pendiente`, datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
export function useResolverAjuste() {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: ({ id, decision, comentario }: { id: string; decision: 'aprobar' | 'rechazar'; comentario?: string | undefined }) => post<AjusteDTO>(`/inventario/ajustes/${id}/${decision}`, { comentario }), onSuccess: () => inv() });
}
export function useDespiece() {
  const inv = useInvalidarInventario();
  const enviar = useEnvioUnico((d: DespieceInput, clave: string) => post<AjusteDTO>('/inventario/despiece', d, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
export function useCrearConteo() {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: (d: { almacen_id: string; observaciones?: string }) => post<ConteoDTO>('/inventario/conteos', d), onSuccess: () => inv() });
}
export function useRegistrarFisico(id: string) {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: (lineas: { producto_id: string; fisico: string }[]) => put<ConteoDTO>(`/inventario/conteos/${id}/fisico`, { lineas }), onSuccess: () => inv() });
}
export function useConfirmarConteo(id: string) {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: (forzar: boolean) => post<ConteoDTO>(`/inventario/conteos/${id}/confirmar`, { forzar }), onSuccess: () => inv() });
}

// Piezas de vidrio y productos con detalles: se reportan con foto, la dueña aprueba y pone precio; se venden por unidades
export const usePiezas = (f: { q?: string | undefined; estado?: string | undefined; clase?: 'vidrio' | 'detalle' | undefined }) =>
  useQuery({ queryKey: ['inventario', 'piezas', f], queryFn: () => get<PiezaDTO[]>('/inventario/piezas', q(f)), placeholderData: (p) => p });
export const useDespiecesVidrio = (habilitado: boolean) => useQuery({ queryKey: ['inventario', 'despieces-vidrio'], queryFn: () => get<DespieceVidrioDTO[]>('/inventario/despieces-vidrio'), enabled: habilitado });
/** La foto del reporte (no es un archivo público: llega en data URL a quien ve el reporte). */
export const useFotoReporte = (id: string, habilitado: boolean) =>
  useQuery({ queryKey: ['inventario', 'despieces-vidrio', id, 'foto'], queryFn: () => get<{ imagen: string | null; imagenes?: string[] }>(`/inventario/despieces-vidrio/${id}/foto`), enabled: habilitado, staleTime: Infinity });
export function useRegistrarDespieceVidrio() {
  const inv = useInvalidarInventario();
  const enviar = useEnvioUnico((d: DespieceVidrioInput, clave: string) => post<DespieceVidrioDTO>('/inventario/despieces-vidrio', d, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
export function useRegistrarProductoDetalle() {
  const inv = useInvalidarInventario();
  const enviar = useEnvioUnico((d: ProductoDetalleInput, clave: string) => post<DespieceVidrioDTO>('/inventario/productos-detalle', d, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
type ResolverDespieceVidrio = { id: string; decision: 'aprobar'; datos: DespieceVidrioAprobarInput } | { id: string; decision: 'rechazar'; datos: { comentario: string } };
export function useResolverDespieceVidrio() {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: ({ id, decision, datos }: ResolverDespieceVidrio) => post<DespieceVidrioDTO>(`/inventario/despieces-vidrio/${id}/${decision}`, datos), onSuccess: () => inv() });
}
export function useEditarPieza() {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: ({ id, precio_divisa }: { id: string; precio_divisa: string }) => patch<PiezaDTO>(`/inventario/piezas/${id}`, { precio_divisa }), onSuccess: () => inv() });
}
export function useSalidaPieza() {
  const inv = useInvalidarInventario();
  // Con la clave de operación: si la respuesta se pierde y se reintenta, la baja (o la vuelta a la existencia) no se aplica dos veces
  const enviar = useEnvioUnico((d: { id: string; datos: PiezaSalidaInput }, clave: string) => post<PiezaDTO>(`/inventario/piezas/${d.id}/salida`, d.datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}

// ─── Retiro en otra sucursal (Fase 4 de retiros, 2026-10-29) ────────────────
type FiltrosOrdenes = { ver?: 'venta' | 'retiro' | undefined; abiertas?: boolean | undefined; estado?: string | undefined; q?: string | undefined; limite?: number };
export const useOrdenesRetiro = (f: FiltrosOrdenes, habilitado = true) =>
  useQuery({ queryKey: ['inventario', 'ordenes-retiro', f], queryFn: () => get<Pagina<OrdenRetiroDTO>>('/inventario/ordenes-retiro', q({ ...f, abiertas: f.abiertas ? 'true' : undefined })), placeholderData: (p) => p, enabled: habilitado });
export const useOrdenRetiro = (id: string | null) => useQuery({ queryKey: ['inventario', 'orden-retiro', id], queryFn: () => get<OrdenRetiroDTO>(`/inventario/ordenes-retiro/${id}`), enabled: !!id });
/** Lo pendiente de la venta y los almacenes de las otras sucursales con su disponible (diálogo de la orden). */
export const useOpcionesRetiro = (documentoId: string | null) =>
  useQuery({ queryKey: ['inventario', 'retiro-opciones', documentoId], queryFn: () => get<RetiroOtraSucursalOpcionesDTO>(`/inventario/documentos/${documentoId}/retiro-otra-sucursal`), enabled: !!documentoId });
/** Crear la orden de retiro (sucursal de venta); con la clave de operación. La respuesta trae el código una sola vez. */
export function useCrearOrdenRetiro() {
  const inv = useInvalidarInventario();
  const enviar = useEnvioUnico(({ documentoId, datos }: { documentoId: string; datos: OrdenRetiroInput }, clave: string) => post<OrdenRetiroCreadaDTO>(`/inventario/documentos/${documentoId}/ordenes-retiro`, datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
/** Registrar el retiro en la sucursal de retiro, con el código y la cédula. */
export function useRetirarOtraSucursal() {
  const inv = useInvalidarInventario();
  const enviar = useEnvioUnico(({ ordenId, datos }: { ordenId: string; datos: RetiroOtraSucursalInput }, clave: string) => post<RetiroOtraSucursalDTO>(`/inventario/ordenes-retiro/${ordenId}/retiros`, datos, conClave(clave)));
  return useMutation({ mutationFn: enviar, onSuccess: () => inv() });
}
export function useExtenderOrdenRetiro() {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: ExtenderOrdenRetiroInput }) => post<OrdenRetiroDTO>(`/inventario/ordenes-retiro/${id}/extender`, datos), onSuccess: () => inv() });
}
export function useAnularOrdenRetiro() {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: ({ id, datos }: { id: string; datos: AnularOrdenRetiroInput }) => post<OrdenRetiroDTO & { correo_orden: CorreoAutomaticoDTO }>(`/inventario/ordenes-retiro/${id}/anular`, datos), onSuccess: () => inv() });
}
export function useNuevoCodigoRetiro() {
  const inv = useInvalidarInventario();
  return useMutation({ mutationFn: ({ id, autorizaciones }: { id: string; autorizaciones?: string[] }) => post<OrdenRetiroCreadaDTO>(`/inventario/ordenes-retiro/${id}/codigo`, autorizaciones?.length ? { autorizaciones } : {}), onSuccess: () => inv() });
}
